import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../App';
import axios from 'axios';
import Navbar from '../components/Navbar';
import BankLogo from '../components/BankLogo';
import { ArrowLeft, ArrowRight, Building2, Target, Users, Award, Shield, TrendingUp, CheckCircle, Heart, Zap, Globe, Lock, Trophy, Newspaper, ExternalLink, Linkedin, User } from 'lucide-react';

const AboutPage = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState(null);

  useEffect(() => {
    axios.get(`${API}/about`).then(res => setContent(res.data)).catch(() => {});
  }, []);

  const values = [
    { icon: Heart, title: 'Customer First', desc: 'Every decision we make starts with what\'s best for our customers.', color: '#FF6B35' },
    { icon: Lock, title: 'Trust & Security', desc: 'Bank-grade encryption protects your data at every step.', color: '#004D7A' },
    { icon: Zap, title: 'Speed & Simplicity', desc: 'Apply in minutes, get responses in days — not weeks.', color: '#FFB627' },
    { icon: Globe, title: 'Transparency', desc: 'No hidden charges. No fine print. Just clear information.', color: '#16A34A' }
  ];

  const stats = [
    { value: '6+', label: 'Banking Partners' },
    { value: '14+', label: 'Financial Products' },
    { value: '6', label: 'Smart Calculators' },
    { value: '24/7', label: 'Customer Support' }
  ];

  const partnerBanks = ['State Bank of India', 'HDFC Bank', 'ICICI Bank', 'Axis Bank', 'Punjab National Bank', 'Kotak Mahindra Bank'];

  return (
    <div style={{ backgroundColor: '#FFFFFF', minHeight: '100vh' }}>
      <Navbar />

      {/* Hero */}
      <section style={{ background: 'linear-gradient(135deg, #FFF7ED 0%, #FFFFFF 50%, #EFF6FF 100%)', padding: '5rem 2rem 4rem' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <button 
            onClick={() => navigate('/')}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'none', border: 'none', color: '#FF6B35', cursor: 'pointer', marginBottom: '2rem', fontWeight: '600' }}
            data-testid="back-to-home-button"
          >
            <ArrowLeft size={20} /> Back to Home
          </button>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '3rem', alignItems: 'center' }}>
            <div>
              <div style={{ display: 'inline-block', padding: '0.4rem 1rem', background: '#FF6B35', borderRadius: '9999px', color: 'white', fontWeight: '700', fontSize: '0.8rem', marginBottom: '1.5rem', letterSpacing: '0.05em' }}>
                ABOUT ARTHIK
              </div>
              <h1 style={{ fontSize: 'clamp(2.5rem, 5vw, 3.75rem)', marginBottom: '1.5rem', color: '#0F172A', fontWeight: '800', lineHeight: 1.1 }}>
                Empowering India's <span className="gradient-text-primary">Financial Future</span>
              </h1>
              <p style={{ fontSize: '1.15rem', color: '#475569', marginBottom: '2rem', lineHeight: 1.8 }}>
                Arthik is India's trusted digital marketplace connecting borrowers with the country's top banks. We make finance simple, transparent, and accessible to every Indian.
              </p>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <button onClick={() => navigate('/services')} className="primary-button" style={{ padding: '0.9rem 1.75rem' }} data-testid="about-explore-services-btn">
                  Explore Services <ArrowRight size={18} />
                </button>
                <button onClick={() => navigate('/contact')} className="secondary-button" style={{ padding: '0.9rem 1.75rem' }} data-testid="about-contact-btn">
                  Get in Touch
                </button>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
              {stats.map((stat, idx) => (
                <div key={idx} className="stat-card" style={{ textAlign: 'center', padding: '2rem 1rem' }} data-testid={`about-stat-${idx}`}>
                  <div style={{ fontSize: '2.5rem', fontWeight: '800', color: '#FF6B35', fontFamily: 'Outfit', marginBottom: '0.25rem' }}>
                    {stat.value}
                  </div>
                  <div style={{ color: '#475569', fontSize: '0.875rem', fontWeight: '600' }}>{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section style={{ padding: '5rem 2rem', backgroundColor: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '2rem' }}>
            <div style={{ padding: '2.5rem', background: 'linear-gradient(135deg, #FFF7ED 0%, #FFE4D1 100%)', borderRadius: '1rem', borderLeft: '6px solid #FF6B35' }} data-testid="mission-section">
              <Target size={40} style={{ color: '#FF6B35', marginBottom: '1.5rem' }} />
              <h2 style={{ fontSize: '1.75rem', color: '#0F172A', marginBottom: '1rem' }}>Our Mission</h2>
              <p style={{ color: '#475569', lineHeight: 1.8, fontSize: '1rem' }}>
                {content?.mission || 'Loading...'}
              </p>
            </div>
            <div style={{ padding: '2.5rem', background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)', borderRadius: '1rem', borderLeft: '6px solid #004D7A' }} data-testid="vision-section">
              <TrendingUp size={40} style={{ color: '#004D7A', marginBottom: '1.5rem' }} />
              <h2 style={{ fontSize: '1.75rem', color: '#0F172A', marginBottom: '1rem' }}>Our Vision</h2>
              <p style={{ color: '#475569', lineHeight: 1.8, fontSize: '1rem' }}>
                {content?.vision || 'Loading...'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      {content?.team_members && content.team_members.length > 0 && (
        <section style={{ padding: '5rem 2rem', backgroundColor: '#F8FAFC' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
              <h2 style={{ fontSize: 'clamp(2rem, 4vw, 2.75rem)', color: '#0F172A', marginBottom: '1rem' }}>
                Meet Our <span className="gradient-text-primary">Leadership</span>
              </h2>
              <p style={{ fontSize: '1.1rem', color: '#475569' }}>The people behind Arthik's mission</p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.5rem' }}>
              {content.team_members.map((member, idx) => (
                <div key={idx} className="service-card" style={{ textAlign: 'center' }} data-testid={`team-member-${idx}`}>
                  <div style={{ width: '100px', height: '100px', borderRadius: '50%', background: 'linear-gradient(135deg, #FF6B35 0%, #FFB627 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.25rem', overflow: 'hidden' }}>
                    {member.image_url ? (
                      <img src={member.image_url} alt={member.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <User size={48} style={{ color: 'white' }} />
                    )}
                  </div>
                  <h3 style={{ fontSize: '1.15rem', color: '#0F172A', marginBottom: '0.25rem' }}>{member.name}</h3>
                  <p style={{ fontSize: '0.875rem', color: '#FF6B35', fontWeight: '700', marginBottom: '0.75rem' }}>{member.role}</p>
                  <p style={{ color: '#475569', fontSize: '0.875rem', lineHeight: 1.6, marginBottom: '1rem' }}>{member.bio}</p>
                  {member.linkedin && (
                    <a href={member.linkedin} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: '#0077B5', fontSize: '0.85rem', fontWeight: '600', textDecoration: 'none' }}>
                      <Linkedin size={16} /> LinkedIn
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Awards */}
      {content?.awards && content.awards.length > 0 && (
        <section style={{ padding: '5rem 2rem', backgroundColor: 'white' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
              <Trophy size={48} style={{ color: '#FFB627', marginBottom: '1rem' }} />
              <h2 style={{ fontSize: 'clamp(2rem, 4vw, 2.75rem)', color: '#0F172A', marginBottom: '1rem' }}>
                Awards & <span className="gradient-text-primary">Recognition</span>
              </h2>
              <p style={{ fontSize: '1.1rem', color: '#475569' }}>Recognized by industry leaders for excellence</p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.5rem' }}>
              {content.awards.map((award, idx) => (
                <div key={idx} className="service-card" style={{ position: 'relative', overflow: 'hidden' }} data-testid={`award-${idx}`}>
                  <div style={{ position: 'absolute', top: 0, right: 0, padding: '0.4rem 0.9rem', background: 'linear-gradient(135deg, #FFB627 0%, #FFCC00 100%)', color: 'white', borderRadius: '0 1rem 0 1rem', fontFamily: 'Outfit', fontWeight: '700', fontSize: '0.85rem' }}>
                    {award.year}
                  </div>
                  <Trophy size={32} style={{ color: '#FFB627', marginBottom: '1rem' }} />
                  <h3 style={{ fontSize: '1.15rem', color: '#0F172A', marginBottom: '0.5rem', paddingRight: '3rem' }}>{award.title}</h3>
                  <p style={{ fontSize: '0.875rem', color: '#FF6B35', fontWeight: '700', marginBottom: '0.75rem' }}>{award.issuer}</p>
                  {award.description && <p style={{ color: '#475569', fontSize: '0.875rem', lineHeight: 1.5 }}>{award.description}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Press Coverage */}
      {content?.press_coverage && content.press_coverage.length > 0 && (
        <section style={{ padding: '5rem 2rem', backgroundColor: '#F8FAFC' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
              <Newspaper size={48} style={{ color: '#004D7A', marginBottom: '1rem' }} />
              <h2 style={{ fontSize: 'clamp(2rem, 4vw, 2.75rem)', color: '#0F172A', marginBottom: '1rem' }}>
                Press <span className="gradient-text-primary">Coverage</span>
              </h2>
              <p style={{ fontSize: '1.1rem', color: '#475569' }}>Featured in India's top publications</p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
              {content.press_coverage.map((press, idx) => (
                <a key={idx} href={press.url || '#'} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }} data-testid={`press-${idx}`}>
                  <div className="service-card" style={{ height: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <span style={{ padding: '0.3rem 0.7rem', background: '#004D7A', color: 'white', borderRadius: '0.4rem', fontSize: '0.75rem', fontWeight: '700', letterSpacing: '0.03em' }}>
                        {press.publication}
                      </span>
                      <span style={{ fontSize: '0.85rem', color: '#94A3B8' }}>{press.date}</span>
                    </div>
                    <h3 style={{ fontSize: '1.05rem', color: '#0F172A', marginBottom: '0.75rem', lineHeight: 1.4 }}>{press.headline}</h3>
                    {press.excerpt && <p style={{ color: '#475569', fontSize: '0.875rem', lineHeight: 1.6, marginBottom: '1rem', fontStyle: 'italic' }}>"{press.excerpt}"</p>}
                    {press.url && (
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: '#FF6B35', fontSize: '0.85rem', fontWeight: '600' }}>
                        Read more <ExternalLink size={14} />
                      </div>
                    )}
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Our Values */}
      <section style={{ padding: '5rem 2rem', backgroundColor: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 style={{ fontSize: 'clamp(2rem, 4vw, 2.75rem)', color: '#0F172A', marginBottom: '1rem' }}>
              Our Core <span className="gradient-text-primary">Values</span>
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.5rem' }}>
            {values.map((value, idx) => {
              const Icon = value.icon;
              return (
                <div key={idx} className="service-card" style={{ textAlign: 'center' }} data-testid={`value-${idx}`}>
                  <div style={{ width: '70px', height: '70px', borderRadius: '50%', background: `${value.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.25rem' }}>
                    <Icon size={32} style={{ color: value.color }} />
                  </div>
                  <h3 style={{ fontSize: '1.15rem', color: '#0F172A', marginBottom: '0.75rem' }}>{value.title}</h3>
                  <p style={{ color: '#475569', fontSize: '0.9rem', lineHeight: 1.6 }}>{value.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Partner Banks */}
      <section style={{ padding: '5rem 2rem', backgroundColor: '#F8FAFC' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 style={{ fontSize: 'clamp(2rem, 4vw, 2.75rem)', color: '#0F172A', marginBottom: '1rem' }}>
              Our Banking <span className="gradient-text-primary">Partners</span>
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1.5rem' }}>
            {partnerBanks.map(bankName => (
              <div key={bankName} className="service-card" style={{ textAlign: 'center', padding: '1.5rem' }} data-testid={`partner-bank-${bankName.toLowerCase().replace(/\s+/g, '-')}`}>
                <div style={{ marginBottom: '1rem' }}>
                  <BankLogo bankName={bankName} size={80} />
                </div>
                <div style={{ fontSize: '0.9rem', color: '#475569', fontWeight: '600' }}>{bankName}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: '5rem 2rem', background: 'linear-gradient(135deg, #FF6B35 0%, #E55100 100%)' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: 'clamp(2rem, 4vw, 2.75rem)', color: 'white', marginBottom: '1rem' }}>
            Ready to Get Started?
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.95)', fontSize: '1.15rem', marginBottom: '2rem', maxWidth: '600px', margin: '0 auto 2rem' }}>
            Join thousands of Indians who've transformed their finances with Arthik.
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/services')} style={{ background: 'white', color: '#FF6B35', borderRadius: '0.5rem', padding: '1rem 2rem', fontWeight: '700', fontSize: '1rem', border: 'none', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }} data-testid="about-cta-services-btn">
              Browse Services
            </button>
            <button onClick={() => navigate('/calculators')} style={{ background: 'transparent', color: 'white', borderRadius: '0.5rem', padding: '1rem 2rem', fontWeight: '700', fontSize: '1rem', border: '2px solid white', cursor: 'pointer' }} data-testid="about-cta-calc-btn">
              Try Calculators
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
